import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { assets } from '@opallabs/api/v2'
import { Button, Modal, Toast, toast } from '@opallabs/lapidary'
import { CaptionRow } from './CaptionRow'
import * as styles from './styles'
import {
  AddCaptionsModalProps,
  CaptionAssetReference,
  CaptionRowState,
  CaptionSubmission,
} from './types'

let rowKeyCounter = 0
const nextKey = () => `caption-row-${++rowKeyCounter}`

const toRowState = (caption: CaptionAssetReference): CaptionRowState => ({
  key: nextKey(),
  caption_asset_reference_id: caption.id,
  asset_uuid: null,
  filename: caption.filename,
  full_url: caption.full_url,
  format: caption.format,
  filesize: null,
  language_code: caption.language_code ?? null,
  uploading: false,
})

const isSubmittable = (rows: ReadonlyArray<CaptionRowState>): boolean =>
  rows.every(
    (row) =>
      !row.uploading &&
      row.language_code !== null &&
      (row.caption_asset_reference_id !== null || row.asset_uuid !== null)
  )

const toSubmissions = (
  rows: ReadonlyArray<CaptionRowState>
): ReadonlyArray<CaptionSubmission> =>
  rows.map((row) => ({
    caption_asset_reference_id: row.caption_asset_reference_id,
    asset_uuid: row.asset_uuid,
    filename: row.filename,
    full_url: row.full_url,
    format: row.format,
    filesize: row.filesize,
    language_code: row.language_code,
  }))

const showUploadingToast = (count: number) => {
  toast(
    (props) => (
      <Toast
        {...props}
        title="Uploading captions"
        message={`Uploading ${count} caption ${count === 1 ? 'file' : 'files'}…`}
      />
    ),
    {
      type: 'info',
      toastId: 'add-captions-uploading-toast',
      autoClose: 3000,
      closeOnClick: true,
    }
  )
}

const showUploadErrorToast = (filename: string) => {
  toast(
    (props) => (
      <Toast
        {...props}
        title="Caption upload failed"
        message={`${filename} could not be uploaded. Please try again.`}
      />
    ),
    {
      type: 'error',
      autoClose: 5000,
      closeOnClick: true,
    }
  )
}

export const AddCaptionsModal: React.FC<AddCaptionsModalProps> = ({
  isVisible,
  initialCaptions,
  onCloseModal,
  onSubmit,
}) => {
  const [rows, setRows] = useState<ReadonlyArray<CaptionRowState>>(() =>
    initialCaptions.map(toRowState)
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isVisible) {
      setRows(initialCaptions.map(toRowState))
    }
  }, [isVisible, initialCaptions])

  const updateRow = useCallback(
    (key: string, patch: Partial<CaptionRowState>) => {
      setRows((current) =>
        current.map((row) => (row.key === key ? { ...row, ...patch } : row))
      )
    },
    []
  )

  const removeRow = useCallback((key: string) => {
    setRows((current) => current.filter((row) => row.key !== key))
  }, [])

  const onLanguageChange = useCallback(
    (key: string, languageCode: string | null) => {
      updateRow(key, { language_code: languageCode })
    },
    [updateRow]
  )

  const triggerFilePicker = () => {
    fileInputRef.current?.click()
  }

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const incoming: Array<CaptionRowState> = Array.from(files).map((file) => ({
      key: nextKey(),
      caption_asset_reference_id: null,
      asset_uuid: null,
      filename: file.name,
      full_url: null,
      format: null,
      filesize: null,
      language_code: null,
      uploading: true,
    }))

    setRows((current) => [...current, ...incoming])
    showUploadingToast(files.length)

    await Promise.all(
      Array.from(files).map(async (file, index) => {
        const rowKey = incoming[index].key
        try {
          const document = await assets.create(file)

          const attrs = document.data.attributes
          updateRow(rowKey, {
            asset_uuid: document.data.id,
            filename: attrs.file_name ?? file.name,
            full_url: attrs.url ?? null,
            format: attrs.file_extension ?? null,
            filesize: attrs.bytes ?? null,
            uploading: false,
          })
        } catch {
          removeRow(rowKey)
          showUploadErrorToast(file.name)
        }
      })
    )
  }

  return (
    <Modal.Root
      open={isVisible}
      onOpenChange={(open) => !open && onCloseModal()}
    >
      <Modal.Content
        css={styles.modalContent}
        onEscapeKeyDown={() => onCloseModal()}
      >
        {isVisible && (
          <Fragment>
            <Modal.Title>Add Captions</Modal.Title>
            <VisuallyHidden asChild>
              <Modal.Description>
                Upload translated caption files and pair each with a language.
              </Modal.Description>
            </VisuallyHidden>
            <Modal.Close />
            <Modal.Body css={styles.modalBody}>
              {rows.length > 0 && (
                <div css={styles.captionsContainer}>
                  {rows.map((row) => (
                    <CaptionRow
                      key={row.key}
                      row={row}
                      onLanguageChange={onLanguageChange}
                      onRemove={removeRow}
                    />
                  ))}
                </div>
              )}

              <Button
                buttonText="+ Add caption file"
                css={styles.addCaptionButton}
                name="Add caption file"
                onClick={triggerFilePicker}
                variant="ghost"
              />

              <input
                ref={fileInputRef}
                css={styles.hiddenFileInput}
                type="file"
                multiple={true}
                accept=".srt,.vtt,application/x-subrip,text/vtt"
                onChange={async (event) => {
                  const { files } = event.target
                  await handleFiles(files)
                  event.target.value = ''
                }}
              />
            </Modal.Body>
          </Fragment>
        )}
        <Modal.Footer css={styles.footer}>
          <span css={styles.footerButtons}>
            <Button
              buttonText="Cancel"
              name="Cancel add captions"
              onClick={() => onCloseModal()}
              variant="ghost"
            />
            <Button
              buttonText="Submit"
              disabled={!isSubmittable(rows)}
              name="Submit captions"
              onClick={() => onSubmit(toSubmissions(rows))}
              variant="primary"
            />
          </span>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  )
}
